import { useEffect, useRef, useState, useCallback } from 'react';
import { 
  trapFocus, 
  getFocusableElements, 
  announceToScreenReader,
  handleArrowKeyNavigation 
} from '../utils/accessibility';

/**
 * Hook for managing focus trap
 */
export const useFocusTrap = (isActive: boolean) => {
  const ref = useRef<HTMLElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  
  useEffect(() => {
    if (!isActive || !ref.current) return;
    
    // Store previous focus
    previousFocusRef.current = document.activeElement as HTMLElement;
    
    // Focus first focusable element
    const focusableElements = getFocusableElements(ref.current);
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
    
    // Set up focus trap
    const cleanup = trapFocus(ref.current);
    
    return () => {
      cleanup();
      // Restore previous focus
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    };
  }, [isActive]);
  
  return ref;
};

/**
 * Hook for keyboard navigation
 */
export const useKeyboardNavigation = (
  items: any[],
  options: {
    orientation?: 'vertical' | 'horizontal';
    onSelect?: (item: any, index: number) => void;
    onEscape?: () => void;
    loop?: boolean;
  } = {}
) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const { orientation = 'vertical', onSelect, onEscape, loop = true } = options;
  
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (items.length === 0) return;
    
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (onSelect) {
          onSelect(items[activeIndex], activeIndex);
        }
        break;
        
      case 'Escape':
        if (onEscape) {
          e.preventDefault();
          onEscape();
        }
        break;
        
      default:
        handleArrowKeyNavigation(
          e,
          activeIndex,
          items.length,
          (newIndex) => {
            if (!loop && (newIndex < 0 || newIndex >= items.length)) return;
            setActiveIndex(newIndex);
          },
          orientation
        );
    }
  }, [activeIndex, items, onSelect, onEscape, orientation, loop]);
  
  return {
    activeIndex,
    setActiveIndex,
    handleKeyDown,
    getItemProps: (index: number) => ({
      tabIndex: index === activeIndex ? 0 : -1,
      'aria-selected': index === activeIndex,
      onFocus: () => setActiveIndex(index),
    }),
  };
};

/**
 * Hook for live region announcements
 */
export const useLiveAnnouncement = () => {
  const announce = useCallback((message: string, priority?: 'polite' | 'assertive') => {
    announceToScreenReader(message, priority);
  }, []);
  
  return announce;
};

/**
 * Hook for managing ARIA attributes
 */
export const useAriaAttributes = (props: {
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
  invalid?: boolean;
}) => {
  const { label, description, error, required, invalid } = props;
  const [labelId] = useState(() => `label-${Math.random().toString(36).substr(2, 9)}`);
  const [descriptionId] = useState(() => `desc-${Math.random().toString(36).substr(2, 9)}`);
  const [errorId] = useState(() => `error-${Math.random().toString(36).substr(2, 9)}`);
  
  const ariaAttributes = {
    'aria-labelledby': label ? labelId : undefined,
    'aria-describedby': [
      description && descriptionId,
      error && errorId,
    ].filter(Boolean).join(' ') || undefined,
    'aria-required': required || undefined,
    'aria-invalid': invalid || !!error || undefined,
  };
  
  const labelProps = { id: labelId };
  const descriptionProps = { id: descriptionId };
  const errorProps = { id: errorId, role: 'alert' };
  
  return {
    ariaAttributes,
    labelProps,
    descriptionProps,
    errorProps,
  };
};

/**
 * Hook for managing focus visible state
 */
export const useFocusVisible = () => {
  const [isFocusVisible, setIsFocusVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);
  
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    
    const handleFocus = (e: FocusEvent) => {
      // Check if focus was triggered by keyboard
      if (e.target === element) {
        setIsFocusVisible(true);
      }
    };
    
    const handleBlur = () => {
      setIsFocusVisible(false);
    };
    
    const handleMouseDown = () => {
      setIsFocusVisible(false);
    };
    
    element.addEventListener('focus', handleFocus);
    element.addEventListener('blur', handleBlur);
    element.addEventListener('mousedown', handleMouseDown);
    
    return () => {
      element.removeEventListener('focus', handleFocus);
      element.removeEventListener('blur', handleBlur);
      element.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);
  
  return { ref, isFocusVisible };
};

/**
 * Hook for managing roving tabindex
 */
export const useRovingTabIndex = (items: any[]) => {
  const [focusedIndex, setFocusedIndex] = useState(0);
  
  const getRovingProps = (index: number) => ({
    tabIndex: index === focusedIndex ? 0 : -1,
    onFocus: () => setFocusedIndex(index),
  });
  
  return { focusedIndex, setFocusedIndex, getRovingProps };
};

/**
 * Hook for managing skip links
 */
export const useSkipLinks = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('skip-link')) {
        setIsVisible(true);
      }
    };
    
    const handleBlur = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('skip-link')) {
        setIsVisible(false);
      }
    };
    
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);
    
    return () => {
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
    };
  }, []);
  
  return { isVisible };
};

/**
 * Hook for reduced motion preference
 */
export const useReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };
    
    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);
  
  return prefersReducedMotion;
};

/**
 * Hook for high contrast mode
 */
export const useHighContrast = () => {
  const [prefersHighContrast, setPrefersHighContrast] = useState(
    window.matchMedia('(prefers-contrast: high)').matches
  );
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-contrast: high)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersHighContrast(e.matches);
    };
    
    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);
  
  return prefersHighContrast;
};